import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MerchantAdminApi } from '../Api';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { ArticleSearch, Article } from './ArticleSearch';

interface RewardArticleSelectorProps {
  api: MerchantAdminApi;
  articleId: string | null;
  onArticleSelected: (articleId: string | null) => void;
  initialArticleId?: string | null;
}

export const RewardArticleSelector: React.FC<RewardArticleSelectorProps> = ({
  api,
  articleId,
  onArticleSelected,
  initialArticleId
}) => {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Use a ref to track if we've already fetched the article
  const fetchedArticleId = useRef<string | null>(null);

  const fetchArticle = useCallback(async (id: string) => {
    if (!api || id === fetchedArticleId.current) return;

    setLoading(true);
    try {
      const result = await api.fetchPOSExternalArticlesById([Number(id)]);
      if (result.payload.products && result.payload.products.length > 0) {
        const articleData = result.payload.products[0];
        setArticle({
          id: articleData.id,
          name: articleData.name,
          product_price_lists: articleData.product_price_lists
        });
        fetchedArticleId.current = id;
      }
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (articleId) {
      // Only fetch if we don't already have the article data
      if (!article || article.id !== articleId) {
        fetchArticle(articleId);
      }
    } else {
      setArticle(null);
      fetchedArticleId.current = null;
    }
  }, [articleId, fetchArticle, article]);

  const handleSelectArticle = (selectedArticle: Article) => {
    onArticleSelected(selectedArticle.id);
    setArticle(selectedArticle);
  };

  const handleRevert = () => {
    if (initialArticleId) {
      onArticleSelected(initialArticleId);
      // Reset the fetched article ID to force a new fetch
      fetchedArticleId.current = null;
      // Clear the current article to show loading state
      setArticle(null);
      // Fetch the new article
      fetchArticle(initialArticleId);
    } else {
      onArticleSelected(null);
      setArticle(null);
      fetchedArticleId.current = null;
    }
  };

  const hasChanges = initialArticleId !== articleId;
  const handleSearch = async (term: string) => {
    try {
      const result = await api.searchPOSExternalArticles(term);
      return result.payload.products.map(article => ({
        id: article.id,
        name: article.name,
        product_price_lists: article.product_price_lists
      }));
    } catch (error) {
      console.error('Error searching articles:', error);
      return [];
    }
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <FontAwesomeIcon
          icon={["fal", "spinner"]}
          spin
          className="text-muted"
          style={{ fontSize: '2rem' }}
        />
      </div>
    );
  }

  return (
    <Row className="g-3">
      <Col md={6} className="order-1 order-md-1">
        {article && (
          <Card className="mb-2">
            <Card.Body className='p-1'>
              <Row className="align-items-center">
                <Col>
                  <div className="d-flex align-items-center">
                    <div className="fw-bold">{article.name}</div>
                  </div>
                  <div className="small text-muted">
                    {t('rewardArticleSelector.id')}: {article.id}
                    {article.product_price_lists && article.product_price_lists.length > 0 && (
                      <>
                        <br />
                        {t('rewardArticleSelector.priceLists')}: {article.product_price_lists.join(', ')}
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!article && (
          <Card className="mb-2">
            <Card.Body className="p-3 text-center">
              <div className="mb-2">
                <FontAwesomeIcon
                  icon={["fal", "gift"]}
                  className="text-muted"
                  style={{ fontSize: '2rem' }}
                />
              </div>
              <div className="text-muted">
                {t('rewardArticleSelector.noProducts')}
              </div>
            </Card.Body>
          </Card>
        )}

        {hasChanges && initialArticleId && (
          <div className="text-end mt-2">
            <Button
              variant="outline-secondary"
              size="sm"
              className="border-0"
              onClick={handleRevert}
            >
              <FontAwesomeIcon icon={["fal", "arrow-rotate-left"]} />
            </Button>
          </div>
        )}
      </Col>

      <Col md={6} className="order-2 order-md-2">
        <div style={{ position: 'relative' }}>
          <ArticleSearch
            onSearch={handleSearch}
            onSelect={handleSelectArticle}
            disabledIds={articleId ? [articleId] : []}
            searchPlaceholderKey="rewardArticleSelector.searchPlaceholder"
            searchHelpKey="rewardArticleSelector.searchHelp"
            searchResultsKey="rewardArticleSelector.searchResults"
            idKey="rewardArticleSelector.id"
            priceListsKey="rewardArticleSelector.priceLists"
            selectButtonIcon={["fal", "exchange"]}
            selectButtonVariant="outline-success"
          />
        </div>
      </Col>
    </Row>
  );
}; 